// Публичный клиент: страница дня, встройка, карточка по ссылке и мобильная страница вопроса.
import { renderCard, renderVoteArea } from '../shared/render.ts';
import type { PublicPost, FeedEvent, ScreenState } from '../shared/types.ts';
import { STATUS_LABELS } from '../shared/types.ts';
import { esc } from '../shared/html.ts';

interface Boot {
  surface: 'site' | 'embed' | 'qr';
  channel?: 'screen' | 'online';
  broadcast: { id: string; status: keyof typeof STATUS_LABELS; textTitle: string; videoTitle: string; video: { playerUrl: string } | null };
  eventCursor?: number; nextCursor?: string | null;
  posts?: PublicPost[]; question?: PublicPost | null; single?: PublicPost;
  allowedParents?: string[];
}

const boot: Boot = JSON.parse(document.getElementById('gradus-boot')!.textContent!);
const bid = boot.broadcast.id;
const channel: 'screen' | 'online' = boot.surface === 'qr' ? 'screen' : 'online';

// ---------- Хранилище браузера (может быть недоступно во встройке при блокировке сторонних данных) ----------
const memory = new Map<string, string>();
const storage = {
  get(k: string): string | null { try { return localStorage.getItem(k); } catch { return memory.get(k) ?? null; } },
  set(k: string, v: string) { try { localStorage.setItem(k, v); } catch { memory.set(k, v); } },
};
function randomKey(): string {
  const b = new Uint8Array(18); crypto.getRandomValues(b);
  return btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function stableKey(name: string): string {
  let k = storage.get(name);
  if (!k || !/^[A-Za-z0-9_-]{16,64}$/.test(k)) { k = randomKey(); storage.set(name, k); }
  return k;
}
// Ключ участника выдаёт сервер (подписанный): браузер не может придумать его сам. Получаем заранее, при открытии страницы.
const TOKEN_KEY = 'gradus:voter-token';
let voterToken: string | null = storage.get(TOKEN_KEY);
let tokenPromise: Promise<string | null> | null = null;
function ensureToken(): Promise<string | null> {
  if (voterToken) return Promise.resolve(voterToken);
  tokenPromise ??= fetch('/api/v1/voter-token', { method: 'POST' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => { if (d?.token) { voterToken = d.token; storage.set(TOKEN_KEY, d.token); } return voterToken; })
    .catch(() => null)
    .finally(() => { tokenPromise = null; });
  return tokenPromise;
}
void ensureToken();
const readerKey = stableKey('gradus:reader');
type MyVote = { optionId?: string; probability?: number };
const myVotes: Record<string, MyVote> = (() => { try { return JSON.parse(storage.get('gradus:votes') ?? '{}'); } catch { return {}; } })();
function rememberVote(postId: string, v: MyVote) { myVotes[postId] = v; storage.set('gradus:votes', JSON.stringify(myVotes)); }

// ---------- Родительский origin встройки ----------
function parentOrigin(): string | null {
  if (boot.surface !== 'embed' || window.parent === window) return null;
  const allowed = boot.allowedParents ?? [];
  const cands: string[] = [];
  const ao = (location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins;
  if (ao && ao.length) cands.push(ao[0]);
  try { if (document.referrer) cands.push(new URL(document.referrer).origin); } catch { /* ignore */ }
  return cands.find((o) => allowed.includes(o)) ?? null;
}
const parent = parentOrigin();

// ---------- Состояние ----------
const posts = new Map<string, PublicPost>();
for (const p of boot.posts ?? []) posts.set(p.id, p);
if (boot.question) posts.set(boot.question.id, boot.question);
if (boot.single) posts.set(boot.single.id, boot.single);
let questionId: string | null = boot.question?.id ?? null;
let lastSeq = boot.eventCursor ?? 0;
let nextCursor = boot.nextCursor ?? null;

const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector<T>(sel);
const feedEl = $('[data-feed]');
const onairSection = $('#onair');
const onairEl = $('[data-onair]');
const freshBtn = $<HTMLButtonElement>('[data-fresh]');
const emptyEl = $('[data-empty]');
const connEl = $('[data-conn]');

function htmlToEl(html: string): HTMLElement {
  const t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild as HTMLElement;
}

function cardFor(p: PublicPost, mode: 'feed' | 'single' | 'question'): HTMLElement {
  // На отдельной карточке и странице вопроса по QR заголовок записи — h1 страницы.
  const level = mode === 'single' || (mode === 'question' && boot.single) ? 1 : 3;
  return htmlToEl(renderCard(p, { mode, voted: myVotes[p.id] ?? null, level }));
}

/** Перерисовать все представления записи: лента, «Вопрос эфира», отдельная карточка. Один ID — одно состояние голоса. */
function refreshPost(id: string): void {
  const p = posts.get(id);
  document.querySelectorAll<HTMLElement>(`[data-post-id="${CSS.escape(id)}"]`).forEach((el) => {
    if (!p) { el.remove(); return; }
    const mode = (el.dataset.mode ?? 'feed') as 'feed' | 'single' | 'question';
    const active = el.contains(document.activeElement);
    const n = cardFor(p, mode);
    el.replaceWith(n);
    observe(n);
    if (active) (n.querySelector<HTMLElement>('[data-vote-area] button, [data-vote-area] input') ?? n).focus({ preventScroll: true });
  });
}
function refreshVoteArea(id: string): void {
  const p = posts.get(id);
  if (!p) return;
  document.querySelectorAll<HTMLElement>(`[data-post-id="${CSS.escape(id)}"]`).forEach((el) => {
    const area = el.querySelector('[data-vote-area]');
    // Не сбрасываем незавершённый ввод шкалы, если человек ещё не ответил: обновляем только блок результатов.
    if (area && el.contains(document.activeElement) && !myVotes[id] && p.state === 'open') {
      const fresh = htmlToEl(`<div>${renderVoteArea(p, { mode: el.dataset.mode as 'feed', voted: null })}</div>`);
      // Шанс, график и итог обновляются, а незавершённый ввод ползунка — нет.
      for (const sel of ['.mkt__head', '.result-total']) {
        const o = area.querySelector(sel), n = fresh.querySelector(sel);
        if (o && n) o.replaceWith(n);
      }
      return;
    }
    if (area) area.outerHTML = renderVoteArea(p, { mode: el.dataset.mode as 'feed', voted: myVotes[id] ?? null });
    el.classList.toggle('is-voted', !!myVotes[id]);
  });
}

function sortKey(p: PublicPost): string { return `${p.publishedAt}|${p.id}`; }

/** Вставка с сохранением положения читателя: элемент-якорь в видимой области остаётся на месте. */
function withAnchor(fn: () => void): void {
  const cards = feedEl ? [...feedEl.children] as HTMLElement[] : [];
  const anchor = cards.find((c) => c.getBoundingClientRect().bottom > 0);
  const before = anchor?.getBoundingClientRect().top;
  fn();
  if (anchor && before !== undefined && anchor.isConnected && window.scrollY > 0) {
    const delta = anchor.getBoundingClientRect().top - before;
    if (delta) window.scrollBy(0, delta);
  }
}

let freshCount = 0;
function upsertFeedCard(p: PublicPost, isNew: boolean): void {
  if (!feedEl) return;
  const existing = feedEl.querySelector<HTMLElement>(`[data-post-id="${CSS.escape(p.id)}"]`);
  if (existing) { refreshPost(p.id); return; }
  const el = cardFor(p, 'feed');
  const key = sortKey(p);
  const after = ([...feedEl.children] as HTMLElement[]).find((c) => {
    const o = posts.get(c.dataset.postId!); return o && sortKey(o) < key;
  });
  // Если нашей записи место ниже последней загруженной страницы — не вставляем (она придёт с «Показать ранние»).
  if (!after && nextCursor) return;
  withAnchor(() => { feedEl.insertBefore(el, after ?? null); });
  observe(el);
  if (isNew && window.scrollY > 240) {
    freshCount++;
    if (freshBtn) { freshBtn.hidden = false; freshBtn.textContent = `↑ Новые записи: ${freshCount}`; }
  } else if (isNew) {
    el.classList.add('is-fresh');
  }
  if (emptyEl) emptyEl.hidden = feedEl.children.length > 0;
}
freshBtn?.addEventListener('click', () => {
  freshCount = 0; freshBtn.hidden = true;
  window.scrollTo({ top: (feedEl?.getBoundingClientRect().top ?? 0) + window.scrollY - 80, behavior: 'smooth' });
});
window.addEventListener('scroll', () => {
  if (window.scrollY < 120 && freshCount) { freshCount = 0; if (freshBtn) freshBtn.hidden = true; }
}, { passive: true });

function removePost(id: string): void {
  posts.delete(id);
  withAnchor(() => document.querySelectorAll(`[data-post-id="${CSS.escape(id)}"]`).forEach((el) => {
    if ((el as HTMLElement).dataset.mode === 'single') {
      el.replaceWith(htmlToEl('<p class="gone">Запись снята с публикации.</p>'));
    } else el.remove();
  }));
  if (questionId === id) setQuestion(null);
  if (emptyEl && feedEl) emptyEl.hidden = feedEl.children.length > 0;
}

async function setQuestion(id: string | null): Promise<void> {
  questionId = id;
  if (!onairSection || !onairEl) return;
  if (!id) { onairSection.hidden = true; onairEl.innerHTML = ''; return; }
  let p = posts.get(id);
  if (!p) {
    const r = await fetch(`/api/v1/posts/${encodeURIComponent(id)}`);
    if (!r.ok) return;
    p = (await r.json()).post as PublicPost;
    posts.set(p.id, p);
  }
  if (questionId !== id) return;
  onairEl.innerHTML = '';
  onairEl.appendChild(cardFor(p, 'question'));
  onairSection.hidden = false;
  postHeight();
}

// ---------- Голосование ----------
async function sendVote(postId: string, body: Record<string, unknown>, el: HTMLElement): Promise<void> {
  el.setAttribute('aria-busy', 'true');
  el.querySelectorAll('button').forEach((b) => (b.disabled = true));
  const payload: Record<string, unknown> = { channel, surface: boot.surface, parentOrigin: parent, ...body };
  let lastErr = 'Не удалось отправить ответ';
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      payload.voterToken = await ensureToken();
      const r = await fetch(`/api/v1/posts/${encodeURIComponent(postId)}/votes`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (r.ok) {
        rememberVote(postId, data.yourVote);
        // Ответ доказывает, что карточку видели: засчитываем просмотр, даже если человек нажал раньше, чем через секунду.
        track('view', postId, true);
        const p = posts.get(postId);
        if (p) { p.result = data.result; p.state = data.state; }
        refreshVoteArea(postId);
        announce(data.duplicate ? 'Вы уже отвечали — учтён первый ответ' : 'Ответ учтён');
        return;
      }
      lastErr = data?.error?.message ?? lastErr;
      // Ключ устарел или испорчен — берём новый и повторяем (это новый участник: старый ключ утерян).
      if (r.status === 401 && data?.error?.code === 'bad_token') { voterToken = null; continue; }
      if (r.status === 409 && data?.error?.code === 'voting_closed') {
        const p = posts.get(postId); if (p) p.state = 'closed';
        refreshVoteArea(postId);
        announce(lastErr);
        return;
      }
      if (r.status < 500 && r.status !== 429) break;
    } catch { /* сетевой сбой — повтор с тем же ключом не добавит голос */ }
    await new Promise((res) => setTimeout(res, 500 * 2 ** attempt));
  }
  el.removeAttribute('aria-busy');
  el.querySelectorAll('button').forEach((b) => (b.disabled = false));
  announce(lastErr);
}

const live = document.createElement('div');
live.className = 'sr-only'; live.setAttribute('role', 'status'); live.setAttribute('aria-live', 'polite');
document.body.appendChild(live);
function announce(t: string) { live.textContent = ''; setTimeout(() => (live.textContent = t), 30); toast(t); }
let toastTimer = 0;
function toast(t: string) {
  let el = $('.toast');
  if (!el) { el = document.createElement('div'); el.className = 'toast'; el.setAttribute('aria-hidden', 'true'); document.body.appendChild(el); }
  el.textContent = t; el.classList.add('is-on');
  clearTimeout(toastTimer); toastTimer = window.setTimeout(() => el!.classList.remove('is-on'), 2400);
}

document.addEventListener('click', async (e) => {
  const t = e.target as HTMLElement;
  const opt = t.closest<HTMLButtonElement>('[data-vote-option]');
  if (opt) {
    const card = opt.closest<HTMLElement>('[data-post-id]')!;
    await sendVote(card.dataset.postId!, { optionId: opt.dataset.voteOption }, card);
    return;
  }
  // «Поделиться»: копирует постоянную ссылку на запись (предпросмотр в мессенджерах берётся из og-метаданных ссылки).
  const share = t.closest<HTMLButtonElement>('[data-share]');
  if (share) {
    const card = share.closest<HTMLElement>('[data-post-id]')!;
    const url = share.dataset.url ?? posts.get(card.dataset.postId!)?.permalink;
    if (!url) return;
    try { await navigator.clipboard.writeText(url); }
    catch { window.prompt('Скопируйте ссылку:', url); return; }
    const label = share.querySelector('span');
    if (label) { label.textContent = 'Ссылка скопирована'; share.classList.add('is-done'); setTimeout(() => { label.textContent = 'Поделиться'; share.classList.remove('is-done'); }, 1800); }
    announce('Ссылка скопирована');
    track('share_link', card.dataset.postId!, true);
  }
});
document.addEventListener('submit', async (e) => {
  const form = (e.target as HTMLElement).closest<HTMLFormElement>('[data-pred-form]');
  if (!form) return;
  e.preventDefault();
  const card = form.closest<HTMLElement>('[data-post-id]')!;
  const v = Number((form.querySelector('[data-pred-input]') as HTMLInputElement).value);
  await sendVote(card.dataset.postId!, { probability: v }, card);
});
document.addEventListener('input', (e) => {
  const inp = (e.target as HTMLElement).closest<HTMLInputElement>('[data-pred-input]');
  if (!inp) return;
  const out = inp.form?.querySelector('[data-pred-out]');
  if (out) out.textContent = `${inp.value}%`;
  inp.style.setProperty('--v', `${inp.value}%`);
});

// ---------- Аналитика: просмотр = карточка видна ≥ 50% не меньше секунды ----------
const pageId = randomKey().slice(0, 12);
const queue: { eventId: string; postId?: string; kind: string }[] = [];
const seenViews = new Set<string>();
let shareSeq = 0;
function track(kind: string, postId: string, flush = false) {
  if (kind === 'view') { if (seenViews.has(postId)) return; seenViews.add(postId); }
  queue.push({ eventId: `${pageId}-${kind === 'view' ? 'v' : `s${shareSeq++}`}-${postId}`, postId, kind });
  if (flush) sendAnalytics();
}
function sendAnalytics(beacon = false) {
  if (!queue.length) return;
  const events = queue.splice(0, 50);
  const body = JSON.stringify({ readerKey, voterToken, surface: boot.surface, parentOrigin: parent, events });
  const url = `/api/v1/broadcasts/${encodeURIComponent(bid)}/analytics`;
  if (beacon && navigator.sendBeacon) { navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' })); return; }
  fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body, keepalive: true })
    .catch(() => { queue.unshift(...events); }); // повтор с теми же eventId — сервер не посчитает дважды
}
setInterval(() => sendAnalytics(), 3000);
addEventListener('pagehide', () => sendAnalytics(true));
const timers = new Map<Element, number>();
const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  for (const en of entries) {
    const el = en.target as HTMLElement;
    if (en.isIntersecting && en.intersectionRatio >= 0.5) {
      if (!timers.has(el)) timers.set(el, window.setTimeout(() => { timers.delete(el); if (el.isConnected) track('view', el.dataset.postId!); }, 1000));
    } else { clearTimeout(timers.get(el)); timers.delete(el); }
  }
}, { threshold: [0, 0.5, 1] }) : null;
function observe(el: HTMLElement) { if (io && el.dataset.postId) io.observe(el); }
document.querySelectorAll<HTMLElement>('[data-post-id]').forEach(observe);

// ---------- Поток событий ----------
function setConn(text: string) { if (connEl) connEl.textContent = text; }

function applyEvent(ev: FeedEvent & { broadcast?: Boot['broadcast'] }): void {
  if (ev.seq <= lastSeq) return; // идемпотентность: повторная доставка ничего не меняет
  lastSeq = ev.seq;
  switch (ev.kind) {
    case 'post.published':
    case 'post.updated': {
      const p = ev.post!;
      const known = posts.get(p.id);
      if (known && known.version > p.version) return;
      posts.set(p.id, p);
      if (!boot.single) upsertFeedCard(p, ev.kind === 'post.published' && !known);
      refreshPost(p.id);
      break;
    }
    case 'post.unpublished': removePost(ev.postId!); break;
    case 'post.results': {
      const p = posts.get(ev.postId!);
      if (p) { p.result = ev.result; p.state = ev.state; if (ev.history) p.history = ev.history; refreshVoteArea(p.id); }
      break;
    }
    case 'screen.changed': {
      const s = ev.screen as ScreenState | null;
      const id = s?.question?.id ?? null;
      if (id !== questionId) void setQuestion(id);
      break;
    }
    case 'broadcast.updated' as FeedEvent['kind']: {
      const b = ev.broadcast!;
      const st = $('[data-bstatus]');
      if (st) st.innerHTML = b.status === 'upcoming' ? '<span class="sr-only">Предстоит</span>' : `<span class="status status--${b.status}">${STATUS_LABELS[b.status]}</span>`;
      const fh = $('#feed-h'); if (fh) fh.textContent = b.textTitle;
      const vh = $('#video-h'); if (vh) vh.textContent = b.videoTitle;
      const frame = $<HTMLIFrameElement>('.video__frame iframe');
      if (frame && b.video && frame.src !== b.video.playerUrl) frame.src = b.video.playerUrl;
      if ((!!frame) !== (!!b.video)) location.reload();
      break;
    }
  }
  postHeight();
}

async function reloadSnapshot(): Promise<void> {
  const r = await fetch(`/api/v1/broadcasts/${encodeURIComponent(bid)}/feed`);
  if (!r.ok) return;
  const page = await r.json() as { items: PublicPost[]; nextCursor: string | null; eventCursor: number };
  lastSeq = page.eventCursor; nextCursor = page.nextCursor;
  if (feedEl) {
    for (const el of [...feedEl.children]) posts.delete((el as HTMLElement).dataset.postId!);
    withAnchor(() => { feedEl.innerHTML = page.items.map((p) => renderCard(p, { voted: myVotes[p.id] ?? null })).join(''); });
    feedEl.querySelectorAll<HTMLElement>('[data-post-id]').forEach(observe);
    if (emptyEl) emptyEl.hidden = page.items.length > 0;
    const more = $('[data-more]'); if (more) more.hidden = !nextCursor;
  }
  for (const p of page.items) posts.set(p.id, p);
  const s = await fetch(`/api/v1/broadcasts/${encodeURIComponent(bid)}/screen`).then((x) => x.ok ? x.json() : null).catch(() => null);
  await setQuestion(s?.question?.id ?? null);
}

let es: EventSource | null = null;
let retry = 0;
function connect(): void {
  es?.close();
  es = new EventSource(`/api/v1/broadcasts/${encodeURIComponent(bid)}/events?after=${lastSeq}`);
  es.addEventListener('ready', () => { retry = 0; setConn(''); document.body.dataset.live = '1'; });
  es.addEventListener('change', (m) => { try { applyEvent(JSON.parse((m as MessageEvent).data)); } catch (err) { console.error(err); } });
  es.addEventListener('reset', async () => {
    // История событий истекла — берём свежий снимок и продолжаем с его курсора.
    es?.close(); setConn('Обновляем ленту…');
    await reloadSnapshot();
    connect();
  });
  es.onerror = () => {
    setConn('Нет связи — переподключаемся…');
    document.body.dataset.live = '0';
    // Браузер переподключается сам и передаёт Last-Event-ID. Если поток закрыт окончательно — открываем заново с нашим курсором.
    if (es?.readyState === EventSource.CLOSED) {
      setTimeout(connect, Math.min(30000, 1000 * 2 ** retry++));
    }
  };
}
// Переподключение всегда с собственным курсором: так повторная доставка после разрыва не теряет и не дублирует события.
addEventListener('online', () => connect());
// Кэш «назад/вперёд»: открытый поток не держим, после возврата переподключаемся с курсора и дополучаем пропущенное.
addEventListener('pagehide', () => { es?.close(); es = null; });
addEventListener('pageshow', (e) => { if (e.persisted) connect(); });

// ---------- «Показать ранние записи» ----------
$('[data-more]')?.addEventListener('click', async (e) => {
  const btn = e.currentTarget as HTMLButtonElement;
  if (!nextCursor) return;
  btn.disabled = true;
  try {
    const r = await fetch(`/api/v1/broadcasts/${encodeURIComponent(bid)}/feed?cursor=${encodeURIComponent(nextCursor)}`);
    const page = await r.json() as { items: PublicPost[]; nextCursor: string | null };
    for (const p of page.items) {
      if (posts.has(p.id) && feedEl?.querySelector(`[data-post-id="${CSS.escape(p.id)}"]`)) continue;
      posts.set(p.id, p);
      const el = cardFor(p, 'feed'); feedEl?.appendChild(el); observe(el);
    }
    nextCursor = page.nextCursor;
    btn.hidden = !nextCursor;
  } finally { btn.disabled = false; postHeight(); }
});

// ---------- Высота встройки для родительской страницы ----------
let lastH = 0;
function postHeight(): void {
  if (!parent) return;
  // Высота содержимого, а не окна: иначе iframe никогда не уменьшится.
  const h = Math.ceil(document.body.getBoundingClientRect().height);
  if (h === lastH) return;
  lastH = h;
  window.parent.postMessage({ type: 'gradus:resize', v: 1, broadcastId: bid, height: h }, parent);
}
if (parent) {
  new ResizeObserver(() => postHeight()).observe(document.body);
  postHeight();
}

// ---------- Старт ----------
// Восстанавливаем «ваш ответ» из локального хранилища.
for (const id of Object.keys(myVotes)) if (posts.has(id)) refreshPost(id);
if (boot.single) {
  // Отдельная карточка и мобильная страница вопроса: живые результаты через поток дня, без ленты.
  lastSeq = 0;
  fetch(`/api/v1/broadcasts/${encodeURIComponent(bid)}/feed?limit=1`).then((r) => r.json()).then((p) => { lastSeq = p.eventCursor; connect(); }).catch(() => connect());
  if (!myVotes[boot.single.id] && (boot.single.type === 'POLL' || boot.single.type === 'PREDICTION')) {
    // Ключ мог сохраниться, а список ответов — нет: спрашиваем сервер.
    fetch(`/api/v1/posts/${encodeURIComponent(boot.single.id)}/my-vote?token=${encodeURIComponent(voterToken ?? '')}`).then((r) => r.json()).then((d) => {
      if (d.vote) { rememberVote(boot.single!.id, d.vote); refreshPost(boot.single!.id); }
    }).catch(() => {});
  }
} else {
  connect();
  if (location.hash.startsWith('#post-')) document.getElementById(location.hash.slice(1))?.focus({ preventScroll: false });
}
document.documentElement.classList.add('js');
void esc;
